import React, { useEffect } from "react";
import { useComponentLogger } from "../../shared/logging";
import { validateProps } from "../../shared/props";
import { ComponentValidator, validateComponent } from "../../shared/validation";
import { useViewport } from "../../styles/media";
import { ImageGradientType } from "../Image/Image.types";
import { ShimmerProgressView } from "../Shimmer/ShimmerProgressView/ShimmerProgressView";
import { FeatureProps } from "./Feature.types";
import { FeatureContent } from "./FeatureContent/FeatureContent";
import { FeatureMediaGroupContent } from "./FeatureContent/FeatureMediaGroupContent";
import { FeatureImage } from "./FeatureImage/FeatureImage";
import { FeatureLayout } from "./FeatureLayout/FeatureLayout";
import { FeatureShimmer } from "./FeatureShimmer/FeatureShimmer";

const featureMediaListLimit = 6;

const mediaListValidator = (mediaListLength: number): ComponentValidator => (
    () => (mediaListLength > featureMediaListLimit ? `Too many media items provided to Feature. ${mediaListLength} were provided but the max allowed is ${featureMediaListLimit}.` : undefined)
);

export const Feature: React.FC<FeatureProps> = ({
    featureHeading,
    imageProps,
    insidePaddingVariant,
    children,
    backgroundType,
    layoutType = "1/2",
    paddingVariant,
    showShadow,
    borderRadius,
    isImageBlockLeft,
    isImageBlockBottom,
    isImageFullBleed,
    isImageGradient,
    isVerticalLayout = ["s", "m"],
    height = "medium",
    ...otherProps
}) => {
    useComponentLogger("Feature", otherProps.id);

    validateProps("Feature", otherProps);

    useEffect(() => {
        if (otherProps.kind === "mediaList") {
            validateComponent("Feature", [mediaListValidator(otherProps.mediaPropsList.length)]);
        }
    }, [otherProps.kind === "mediaList" ? otherProps.mediaPropsList : undefined]);

    const viewportType = useViewport();
    const verticalLayout = isVerticalLayout.includes(viewportType);

    let imageGradientType: (ImageGradientType | undefined);
    if (isImageGradient) {
        if (verticalLayout) {
            imageGradientType = "toBottom";
        } else {
            imageGradientType = "toLeft";
        }
    }

    let contentSlot;

    if (otherProps.kind === "mediaList") {
        contentSlot = (
            <FeatureMediaGroupContent
                featureHeading={featureHeading}
                mediaPropsList={otherProps.mediaPropsList}
                operationData={otherProps.operationData}
            />
        );
    } else if (otherProps.kind === "noBodyContent") {
        contentSlot = (
            <FeatureContent
                featureHeading={featureHeading}
                ctaProps={otherProps.ctaProps}
                operationData={otherProps.operationData}
            />
        );
    } else {
        contentSlot = (
            <FeatureContent
                featureHeading={featureHeading}
                bodyTextVariant={otherProps.bodyTextVariant}
                bodyText={otherProps.bodyText}
                ctaProps={otherProps.ctaProps}
                operationData={otherProps.operationData}
            />
        );
    }

    const featureShimmer = (
        <FeatureShimmer backgroundType="card" height="medium" layoutType="2/3" />
    );

    const baseComponent = (
        <FeatureLayout
            backgroundType={backgroundType}
            layoutType={layoutType}
            isImageBlockLeft={isImageBlockLeft}
            isImageBlockBottom={isImageBlockBottom}
            isImageFullBleed={isImageFullBleed}
            insidePaddingVariant={insidePaddingVariant}
            height={height}
            paddingVariant={paddingVariant}
            showShadow={showShadow}
            borderRadius={borderRadius}
            isVerticalLayout={isVerticalLayout}
            imageSlot={(
                <FeatureImage
                    imageProps={imageProps}
                    height={height}
                    imageFit={isImageFullBleed ? "cover" : "center-contain"}
                    imageGradientType={imageGradientType}
                    operationData={otherProps.operationData}
                />
            )}
            contentSlot={contentSlot}
            id={otherProps.id}
        />
    );

    if (otherProps.operationData) {
        return (
            <ShimmerProgressView
                operationData={otherProps.operationData}
                inertView={featureShimmer}
                firstLoadView={featureShimmer}
                updatingView={featureShimmer}
                finishedView={baseComponent}
                failedView={undefined}
            />
        );
    }
    return baseComponent;
};
